import { channelInfo } from '@op/components/ChannelLabel';
import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { PageInfoType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { useEffect, useMemo, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';

/**
 * 统一 Video 数据获取值映射
 * key 为取值 key，valueKey 为对象实际值
 */
const getCommonVideoValue = (data: any) => {
  return [
    { key: 'views', valueKey: 'tweets_view' },
    { key: 'likes', valueKey: 'tweets_like' },
    { key: 'comment', valueKey: 'tweets_comment' },
    { key: 'followers', valueKey: 'anchors_followers' },
    { key: 'username', valueKey: 'anchor_name' },
    { key: 'cover', valueKey: 'video_cover' },
    { key: 'time', valueKey: 'video_release_time' },
    { key: 'channel', valueKey: 'channel_name' },
    { key: 'duration', valueKey: 'video_duration' },
    { key: 'url', valueKey: 'video_url' },
  ].reduce((pre, cur) => {
    const { key, valueKey } = cur;
    return {
      ...pre,
      [key]: data[valueKey],
    };
  }, data);
};

export type TApiFunction = (
  params: any,
) => Promise<{ list: any[]; total: number }>;

interface IUseVideoBaseDataParams {
  filter: any;
  apiFunction: TApiFunction;
  defaultPageInfo?: { page: number; page_size: number };
  translateCategory?: (value: string, isEn: boolean) => string;
  useFieldMapping?: boolean;
  defaultSortKey?: string;
  downloadKey?: 'MarketingVideoXDownload' | 'MarketingVideoDownload';
  channel?: 'twitter' | 'youtube' | 'tiktok' | 'bilibili';
}

function getListData(data: any[][]) {
  const result: Array<any> = [];
  let page = 1;
  data.every(item => {
    if (item) {
      result.push(...item);
      page = page + 1;
      return true;
    }
    return false;
  });
  return { page, result };
}

export function useVideoBaseData({
  filter,
  apiFunction,
  defaultPageInfo = { page: 1, page_size: 40 },
  translateCategory,
  useFieldMapping = false,
  defaultSortKey = 'tweets_view',
  downloadKey = 'MarketingVideoDownload',
  channel,
}: IUseVideoBaseDataParams) {
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: defaultSortKey,
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({
    defaultPageInfo,
  });
  const [dataRef, data, setData] = useStateRef<any[][]>([]);
  const [total, setTotal] = useState(0);
  const [maxMetrics, setMaxMetrics] = useState<Record<string, number>>({});
  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  function resetData() {
    setData([]);
    setMaxMetrics({});
    resetPageInfo();
  }

  function changeSortBy(sortByValue: SortBy<string>) {
    setSortBy(sortByValue);
    resetData();
  }

  function changeKeyword(keywordValue: string) {
    setKeyword(keywordValue);
    resetData();
  }

  const params = useMemo(() => {
    return {
      ...filter,
      ...pageInfo,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
      channels: [channel],
    };
  }, [pageInfo]);

  function request() {
    if (!filter) return;
    wrapperCancelRequest(apiFunction, params)
      .then((data: any) => {
        const { list, total, max } = data;
        let realList = list || [];
        if (useFieldMapping) {
          realList = realList.map((item: any) => getCommonVideoValue(item));
        }

        if (dataRef.current) {
          dataRef.current[pageInfo.page - 1] = realList;
          setData([...dataRef.current]);
        }

        setTotal(total || 0);
        if (max) {
          setMaxMetrics(max);
        }
      })
      .catch(() => {
        const { page } = getListData(dataRef.current || []);
        if (page > 0) {
          changePageInfoNoRender({ ...pageInfo, page: page - 1 });
        }
      });
  }

  useEffect(() => {
    const { page } = getListData(dataRef.current || []);
    if (pageInfo.page >= page) {
      request();
    }
  }, [params]);

  function download() {
    if (!translateCategory) return;
    const { categories } = params;
    const { name } = channelInfo(channel as "youtube");
    gotoDownload({
      key: downloadKey,
      fileName: `Videos_${name}_${translateCategory?.(
        categories?.[0],
        true,
      )}_${DBO_TM.timeTitle(params)}`,
      filter: params,
      auxiliaryParams: { channel },
    });
  }

  function changePageInfoData(localPageInfo: PageInfoType) {
    if (pageInfo.page_size !== localPageInfo.page_size) {
      setData([]);
    }
    changePageInfo(localPageInfo);
  }

  const [operation, setOperation] = useState<'card' | 'table'>('card');

  function nextPageChange() {
    const { page, page_size } = pageInfo;
    if (loading || total <= page * page_size) {
      return;
    }
    if (operation === 'table') {
      return;
    }
    nextPage();
  }

  function changeOperation(value: 'card' | 'table') {
    setOperation(value);
    if (value === 'table') {
      changePageInfoNoRender({ ...pageInfo, page: 1 });
    } else {
      const { page } = getListData(dataRef.current || []);
      changePageInfoNoRender({ ...pageInfo, page: page - 1 });
    }
  }

  const cardRecords = useMemo(() => {
    return getListData(data || []).result;
  }, [data, pageInfo]);

  const tableRecords = useMemo(() => {
    return data?.[pageInfo.page - 1] || [];
  }, [data, pageInfo]);

  const records = useMemo(() => {
    return getListData(data || []).result;
  }, [data]);

  return {
    data,
    records,
    cardRecords,
    tableRecords,
    total,
    pageInfo,
    changePageInfo: changePageInfoData,
    nextPage: nextPageChange,
    loading,
    sortBy,
    keyword,
    setKeyword: changeKeyword,
    setSortBy: changeSortBy,
    download,
    operation,
    setOperation: changeOperation,
    resetData,
    maxMetrics,
  };
}
